import { joinTodoMemo } from '../components/todoNote/todoNoteRowShared';
import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';
import {
  findBudgetTextOccurrences,
  normalizeBudgetSearchQuery,
  textIncludesBudgetSearchQuery,
} from './budgetNoteSearch';

export type TodoNoteSearchField = 'memo' | 'misc' | 'category' | 'category_note';

export type TodoNoteSearchMatch = {
  matchId: string;
  field: TodoNoteSearchField;
  itemId: string | null;
  categoryId: string | null;
  start: number;
  end: number;
};

export function buildTodoItemFieldSearchMatchId(
  itemId: string,
  field: 'memo' | 'misc' | 'category',
  start: number
): string {
  return `todo:item:${itemId}:${field}:${start}`;
}

export function buildTodoCategoryNoteSearchMatchId(categoryId: string, start: number): string {
  return `todo:cat:${categoryId}:note:${start}`;
}

export function todoMemoBodyCharOffset(title: string, body: string): number {
  if (!body) return -1;
  const combined = joinTodoMemo(title, body);
  const split = combined.indexOf('\n');
  if (split === -1) return -1;
  return split + 1;
}

export function buildTodoMemoBodySearchMatchId(
  itemId: string,
  bodyStart: number,
  title: string,
  body: string
): string {
  const offset = todoMemoBodyCharOffset(title, body);
  return buildTodoItemFieldSearchMatchId(itemId, 'memo', offset >= 0 ? offset + bodyStart : bodyStart);
}

export function todoItemCalendarPopSearchHit(
  item: Pick<TodoNoteItemDraft, 'title' | 'body' | 'misc'>,
  query: string
): boolean {
  const q = normalizeBudgetSearchQuery(query);
  if (!q) return false;
  return (
    textIncludesBudgetSearchQuery(item.title, q) ||
    textIncludesBudgetSearchQuery(item.body, q) ||
    textIncludesBudgetSearchQuery(item.misc, q)
  );
}

function pushFieldMatches(
  out: TodoNoteSearchMatch[],
  item: TodoNoteItemDraft,
  field: 'category' | 'memo' | 'misc',
  text: string,
  q: string,
  categoryId: string | null
): void {
  for (const hit of findBudgetTextOccurrences(text, q)) {
    out.push({
      matchId: buildTodoItemFieldSearchMatchId(item.id, field, hit.start),
      field,
      itemId: item.id,
      categoryId,
      start: hit.start,
      end: hit.end,
    });
  }
}

export function collectTodoNoteSearchMatches(params: {
  query: string;
  /** 타임테이블 표시 순(마감일·bucket_order) */
  orderedItems: TodoNoteItemDraft[];
  categories: TodoNoteCategoryDraft[];
  categoryInputByItemId: Record<string, string>;
  filterCategoryId: string | null;
}): TodoNoteSearchMatch[] {
  const q = normalizeBudgetSearchQuery(params.query);
  if (!q) return [];
  const out: TodoNoteSearchMatch[] = [];
  const catById = new Map(params.categories.map((c) => [c.id, c]));

  if (params.filterCategoryId) {
    const cat = catById.get(params.filterCategoryId);
    const note = cat?.note_memo ?? '';
    for (const hit of findBudgetTextOccurrences(note, q)) {
      out.push({
        matchId: buildTodoCategoryNoteSearchMatchId(params.filterCategoryId, hit.start),
        field: 'category_note',
        itemId: null,
        categoryId: params.filterCategoryId,
        start: hit.start,
        end: hit.end,
      });
    }
  }

  for (const item of params.orderedItems) {
    const catText =
      (params.categoryInputByItemId[item.id] ?? '').trim() ||
      (item.category_id ? (catById.get(item.category_id)?.name ?? '') : '');
    pushFieldMatches(out, item, 'category', catText, q, item.category_id);
    pushFieldMatches(out, item, 'memo', joinTodoMemo(item.title, item.body), q, null);
    pushFieldMatches(out, item, 'misc', item.misc ?? '', q, null);
  }

  return out;
}
